import { useEffect, useRef, useState } from "react"
import { formatSecs, iAxios, getActivityCls } from "../../../lib/utils"
import { Spinner, TabFilter } from "../../common/Helper";
import { InsightsDashboard } from "./Insights";
import { useMilestone, usePopupOutsideClose } from "../../../lib/hooks";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Icon from "../../ds/Icon";
import moment from 'moment';
import classNames from "classnames";

const TimeLabel = (props) => {
    let { label, cls, secs, count = 0, username, date = '', expected: passedExpected, devSecs } = props;
    if (!cls) {
        cls = label.toLowerCase();
    }
    let expected = passedExpected || 3600 * 8;
    if (cls === 'break' && !passedExpected) {
        expected = 3600 * 1;
    }
    let perc = Math.floor((secs / (expected || 1)) * 100);
    const color = getActivityCls(cls, secs);

    return <a target="_blank" rel="noreferrer" title={`Total ${count}`} href={`/my-day/${username}?date=${date}`}
        className={`action-tm  ${color}  `} style={{ textDecoration: 'none' }}>
        <div className=" action-prog" title={`${perc} %`}>
            <div className={` action-prog-line`} style={{ width: `${perc > 100 ? 100 : perc}%` }}></div>
        </div>
        <div style={{ whiteSpace: 'nowrap' }}>
            {label} : {formatSecs(secs)}
            {cls === 'in' && <DevLabel devSecs={devSecs} inSecs={secs} />}
        </div>
    </a>
}

/**
 * DevLabel component displays the total developer hours and its percentage 
 * relative to the total "In" duration.
 */
const DevLabel = ({ devSecs, inSecs }) => {
    if (!devSecs || devSecs <= 0) return null;
    const inMins = Math.floor(inSecs / 60);
    const devMins = Math.floor(devSecs / 60);
    const perc = inMins > 0 ? Math.round((devMins / inMins) * 100) : 0;
    return <div style={{ marginTop: '2px' }}>
        <div style={{ background: 'white', margin: '4px 0px' }} title={`${perc}% of In`}>
            <div style={{ background: 'black', height: '6px', width: `${perc > 100 ? 100 : perc}%` }}></div>
        </div>
        <span style={{ fontSize: '0.85em' }}>Dev : {formatSecs(devSecs)} ({perc}%)</span>
    </div>
}

// Comparators keyed by sort mode. avg-per-day = total / day-count for that group.
const avgIn = (r) => (r.totalCount ? r.totalIn / r.totalCount : 0);
const avgDev = (r) => (r.totalCount ? r.totalDev / r.totalCount : 0);
const avgBreak = (r) => (r.brkCount ? r.totalBreak / r.brkCount : 0);
const SORTERS = {
    'name': (a, b) => a.name.localeCompare(b.name),
    'in-desc': (a, b) => avgIn(b) - avgIn(a),
    'in-asc': (a, b) => avgIn(a) - avgIn(b),
    'dev-desc': (a, b) => avgDev(b) - avgDev(a),
    'dev-asc': (a, b) => avgDev(a) - avgDev(b),
    'break-desc': (a, b) => avgBreak(b) - avgBreak(a),
    'break-asc': (a, b) => avgBreak(a) - avgBreak(b),
};
const SORT_OPTIONS = [
    ['name', 'Name (A–Z)'],
    ['in-desc', 'Avg In ↓'],
    ['in-asc', 'Avg In ↑'],
    ['dev-desc', 'Avg Dev ↓'],
    ['dev-asc', 'Avg Dev ↑'],
    ['break-desc', 'Avg Break ↓'],
    ['break-asc', 'Avg Break ↑'],
];

// Activity-class filter options (value is the CSS class toggled on the table).
const CLASS_FILTERS = [
    ['show-good', 'Good'],
    ['show-bad', 'Average'],
    ['show-flagged', 'Flagged'],
];

// Multi-select dropdown for the activity-class filter, matching the design
// system dropdown pattern used by TeamFilter / MilestoneSwitch.
const ClassFilter = ({ value, onChange }) => {
    const dispatch = useDispatch();
    const isOpen = useSelector(st => st.modalStatus.popupName === 'admin-class-filter');
    const ref = useRef(null);
    usePopupOutsideClose(ref, isOpen);

    const toggle = (cls) => {
        onChange(value.includes(cls) ? value.filter(c => c !== cls) : [...value, cls]);
    };
    const allOn = value.length === CLASS_FILTERS.length;
    const selected = CLASS_FILTERS.filter(([c]) => value.includes(c)).map(([, l]) => l);
    const label = selected.length === 0 ? 'None' : allOn ? 'All' : selected.join(', ');

    return (
        <div className="ds-dropdown" ref={ref}>
            <button type="button" className="ds-btn is-soft ds-trigger"
                onClick={() => dispatch({ type: 'popup', name: isOpen ? '' : 'admin-class-filter' })}
                aria-haspopup="true" aria-expanded={isOpen} title="Filter by activity class">
                <span>Status: {label}</span>
                <Icon name="chevronDown" className="ds-trigger__caret" />
            </button>
            <div className={classNames("ds-dropdown__panel", { "is-open": isOpen })}>
                <div className="ds-dropdown__head">
                    <span className="ds-label" style={{ margin: 0 }}>Show categories</span>
                    <button type="button" className="ds-btn is-soft is-sm"
                        onClick={() => onChange(allOn ? [] : CLASS_FILTERS.map(([c]) => c))}>
                        {allOn ? 'Clear' : 'All'}
                    </button>
                </div>
                {CLASS_FILTERS.map(([cls, lbl]) => (
                    <label key={cls} className="ds-dropdown__item" style={{ justifyContent: 'flex-start' }}>
                        <input type="checkbox" className="ds-tree__check"
                            checked={value.includes(cls)} onChange={() => toggle(cls)} />
                        <span>{lbl}</span>
                    </label>
                ))}
            </div>
        </div>
    );
};

const InOutDashboard = () => {
    const [statsData, setStatsData] = useState(() => {
        const cached = localStorage.getItem('dashboard_stats');
        return cached ? JSON.parse(cached) : null;
    });
    const [dateList, setDateList] = useState([]);
    const [classList, setClassList] = useState(['show-good', 'show-bad', 'show-flagged'])
    const [tableData, setTableData] = useState([]);
    const [sortMode, setSortMode] = useState('name');
    // ref so the rebuild effect can re-apply the active sort without re-running
    // every time the sort changes.
    const sortRef = useRef('name');
    const userFilter = useSelector(st => st.tickets.userFilter)
    const milestone = useMilestone();
    // username -> display name (rows are keyed by username; show the real name)
    const userMap = userFilter.reduce((m, u) => { m[u.username] = u.name; return m; }, {});


    useEffect(() => {
        if (!statsData) {
            return;
        }
        const empList = statsData.reduce((acc, item) => {
            if (!acc.includes(item.userId)) {
                acc.push(item.userId)
            }
            return acc;
        }, []);
        empList.sort()
        const dl = statsData.reduce((acc, item) => {
            if (!acc.includes(item.dt)) {
                acc.push(item.dt)
            }
            return acc;
        }, []);
        dl.sort((a, b) => b.localeCompare(a))
        setDateList(dl);
        const data = empList.filter(emp => {
            return userFilter.some(item => item.username === emp)
        }).map(emp => {
            let prevBrkDate = '';
            let prevInDate = '';
            return dl.reduce((acc, dt) => {
                const key = `${dt}-${acc.name}-${acc.content.length}`
                const list = statsData.filter(item => item.userId === emp && item.dt === dt);

                const info = list.map(el => {
                    const secs = parseInt(`${el.actionDuration}`) / 1000;
                    const devSecs = el.devDuration ? parseInt(`${el.devDuration}`) / 1000 : 0;
                    if (el.actionGroup === 'In') {
                        acc.totalIn += secs;
                        // Accumulate developer duration (productive time) from screenshots
                        acc.totalDev += devSecs;
                        if (dt !== prevInDate) {
                            prevInDate = dt;
                            acc.totalCount++;
                        }
                    } else if (el.actionGroup === 'Break') {
                        acc.totalBreak += secs;
                        if (dt !== prevBrkDate) {
                            prevBrkDate = dt;
                            acc.brkCount++;
                        }
                    }
                    return <TimeLabel key={`${key}-${el.actionGroup}`} count={el.actionCount}
                        username={emp} date={dt} label={el.actionGroup} secs={secs}
                        devSecs={el.actionGroup === 'In' ? devSecs : undefined} />
                })

                acc.content.push(<td id={key} key={key}>{info}</td>);
                return acc;
            }, { name: emp, totalBreak: 0, totalIn: 0, totalDev: 0, content: [], brkCount: 0, totalCount: 0 });

        });
        // preserve the active sort across data reloads
        data.sort(SORTERS[sortRef.current] || SORTERS.name);
        setTableData(data);
    }, [statsData, userFilter])

    useEffect(() => {
        if (!milestone) {
            return;
        }

        iAxios.get('/api/v1/admin/user-log-stats', {
            params: {
                start: milestone.start,
                end: milestone.end
            }
        }).then(resp => {
            const stats = resp.data.data.stats;
            setStatsData(stats);
            localStorage.setItem('dashboard_stats', JSON.stringify(stats));
        }).catch(ex => {
            console.error(ex.message);
        });
    }, [milestone])

    const applySort = (mode) => {
        const cmp = SORTERS[mode] || SORTERS.name;
        sortRef.current = mode;
        setSortMode(mode);
        setTableData(prev => [...prev].sort(cmp));
    }

    // clicking the Name header cycles through the sort options
    const cycleSort = () => {
        const idx = SORT_OPTIONS.findIndex(([m]) => m === sortMode);
        const next = SORT_OPTIONS[(idx + 1) % SORT_OPTIONS.length][0];
        applySort(next);
    }

    return <div className="ds-panel">
        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 'var(--ds-space-4)', alignItems: 'center', justifyContent: 'flex-end', padding: 'var(--ds-space-4) var(--ds-space-5)' }}>
            <label className="ds-label" style={{ margin: 0, marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--ds-space-2)', whiteSpace: 'nowrap' }}>
                Sort by
                <select className="ds-select" value={sortMode} onChange={e => applySort(e.target.value)}>
                    {SORT_OPTIONS.map(([m, label]) => <option key={m} value={m}>{label}</option>)}
                </select>
            </label>
            <ClassFilter value={classList} onChange={setClassList} />
        </div>
        {statsData == null && <Spinner />}
        {statsData && <div className="ds-table-wrap">
            <table className={classNames("admin-table scroll-table", classList)}>
                <thead>
                    <tr><th className="sticky-col first-col"
                        onClick={cycleSort} style={{ cursor: 'pointer' }} title={`Current sort: ${sortMode} (click to cycle)`}>
                        Name </th>

                        {dateList.map(item => <th key={item}
                        // style={{transform:'rotate(-90deg)'}}
                        >{moment(item).format('ddd, DD-MMM')}</th>)}
                    </tr>
                </thead>
                <tbody >
                    {tableData.map((info, idx) => {
                        return <tr key={`${info.name}-${idx}`}>
                            <td className="sticky-col first-col">
                                <span className="admin-name">{idx + 1}. {userMap[info.name] || info.name}</span>
                                <br />
                                {info.totalCount > 0 && <TimeLabel label="Avg In" cls='in' username={info.name}
                                    count={info.totalCount} secs={info.totalIn / info.totalCount}
                                    devSecs={info.totalDev / info.totalCount} />}

                                {info.brkCount > 0 && <TimeLabel label="Avg Break" cls='break' username={info.name}
                                    count={info.brkCount} secs={info.totalBreak / info.brkCount} />}
                            </td>
                            {info.content}
                        </tr>
                    })}
                </tbody>
            </table>
        </div>}
    </div>
}

export const AdminDashboard = () => {

    const [sp] = useSearchParams();
    const tab = sp.get('tab') || 'inout';

    return <div>
        <TabFilter filters={['InOut', 'Insights']} name='tab' />
        <div style={{ marginTop: 'var(--ds-space-5)' }}>
            {tab === 'inout' && <InOutDashboard />}
            {tab === 'insights' && <InsightsDashboard />}
        </div>
    </div>;
}